import {Component,Inject,OnInit,ViewChild} from '@angular/core';
import {MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,MatLegacyDialogRef as MatDialogRef} from "@angular/material/legacy-dialog";
import {ParticipantExterne,TypeExterne} from "@domain/participant/participant-externe";
import {NgForm} from "@angular/forms";
import {EntrepriseUtilisateursService} from "@components/admin/entreprise/utilisateurs/entreprise-utilisateurs.service";
import {TranslateService} from "@ngx-translate/core";
import {ToastrService} from "ngx-toastr";
import {finalize,first} from "rxjs/operators";
import {Result,TypeCodeErreur} from "@domain/common/http/result";
import {Alerte,NiveauAlerte} from "@domain/common/alerte/alerte";
import {StatutOubli} from "@domain/user/user-dto";
import {RgpdService} from "@services/admin/entreprise/rgpd.service";

/**
 * Composant de la popin d'édition d'un externe
 */
@Component({
	host: {'data-test-id': 'edit-externe-popin'},
	templateUrl: './edit-externe-popin.component.html'
})
export class EditExternePopinComponent implements OnInit {
	/** Externe */
	externe: ParticipantExterne;

	/** Alerte post-fusion */
	alerteFusion: Alerte;

	/** Raffraîchir la liste à la fermture de la popin */
	refreshAtClosing: boolean = false;

	/** Traitement en cours */
	isPending: boolean = false;

	/** Formulaire */
	@ViewChild('form') form: NgForm;

	/** Import des énums pour le front */
	TypeExterne: typeof TypeExterne = TypeExterne;
	NiveauAlerte: typeof NiveauAlerte = NiveauAlerte;
	StatutOubli: typeof StatutOubli = StatutOubli;

	/** Délai d'oubli en mois */
	delaiMois: number = 120;

	/**
	 * Constructeur
	 *
	 * @param data les données en entrée de la popin
	 * @param userService le service de gestion des utilisateurs
	 * @param translateService le moteur de traduction
	 * @param toastrService le toaster
	 * @param matDialogRef la référence à la popin
	 * @param rgpdService le service de gestion des paramètres RGPD
	 */
	constructor(
		@Inject(MAT_DIALOG_DATA) public data: { externe: ParticipantExterne,editionPostFusion: boolean },
		private userService: EntrepriseUtilisateursService,
		private translateService: TranslateService,
		private toastrService: ToastrService,
		private matDialogRef: MatDialogRef<EditExternePopinComponent>,
		private rgpdService: RgpdService
	) {
	}

	/**
	 * Initialisation du composant
	 */
	ngOnInit(): void {
		//Copie des données en entrée
		this.externe = Object.assign({},this.data.externe);

		//Récupération des paramètres RGPD pour obtenir le délai d'oubli
		this.rgpdService.consultRgpdParams().subscribe((result: Result) => {
			if (result && result.data) {
				//La structure est {rgpdParam: {...}}
				const rgpdParamWrapper = result.data as any;
				if (rgpdParamWrapper.rgpdParam && rgpdParamWrapper.rgpdParam.delaiDroitOubli != null) {
					this.delaiMois = rgpdParamWrapper.rgpdParam.delaiDroitOubli;
				}
			}
			//Calcul du statutOubli et de la dateStatutOubli avec le délai configuré
			this.calculerStatutOubli(this.delaiMois);
		}, (error) => {
			//En cas d'erreur, utiliser le délai par défaut
			this.calculerStatutOubli(this.delaiMois);
		});

		if (this.data.editionPostFusion) {
			//Initialisation de l'alerte
			this.alerteFusion = new Alerte({
				niveau: NiveauAlerte.NO_CONTROL,
				message: this.translateService.instant('admin.entreprise.utilisateurs.externes.editExternePopin.fusionSucces')
			});
		}
	}

	/**
	 * Calcule le statut d'oubli et la date de statut d'oubli basée sur la date de sortie
	 * Utilise la même logique que le backend
	 *
	 * @param delaiMois le délai en mois pour le droit à l'oubli
	 */
	private calculerStatutOubli(delaiMois: number): void {
		const today = new Date();
		today.setHours(0, 0, 0, 0);

		//Statut actif
		if (this.externe.actif) {
			this.externe.statutOubli = StatutOubli.ACTIF;
			this.externe.dateStatutOubli = null;
		} else if (this.externe.dateOut) {
			//Si l'externe a une date de sortie, calculer le statut
			const dateOut = new Date(this.externe.dateOut);
			dateOut.setHours(0, 0, 0, 0);

			const dateStatut = new Date(dateOut);
			//Ajouter le délai en mois (comme le backend)
			dateStatut.setMonth(dateStatut.getMonth() + delaiMois);

			//Toujours calculer dateStatutOubli même si statutOubli est déjà défini
			this.externe.dateStatutOubli = dateStatut;

			if (dateStatut < today || dateStatut.getTime() === today.getTime()) {
				this.externe.statutOubli = StatutOubli.EN_ATTENTE;
			} else {
				this.externe.statutOubli = StatutOubli.PROGRAMME;
			}
		} else {
			//Cas inactif sans date de sortie
			this.externe.statutOubli = StatutOubli.SANS_DATE_SORTIE;
			this.externe.dateStatutOubli = null;
		}
	}

	/**
	 * Promotion de l'externe
	 */
	onPromote(): void {
		//Chargement en cours
		this.isPending = true;

		//Appel au service
		this.userService.promouvoirExterne(this.externe.idParticipantExterne)
			.pipe(first(),finalize(() => this.isPending = false))
			.subscribe((result: Result) => {
				//Vérification du result
				if (result.codeErreur === TypeCodeErreur.NO_ERROR) {
					//Toast succès
					this.toastrService.success(this.translateService.instant('admin.entreprise.utilisateurs.externes.editExternePopin.pomotionSucces'));

					//Passage du type à ADMINISTRATEUR
					this.externe.type = TypeExterne.ADMINISTRATEUR;

					//Passage du flag à true
					this.refreshAtClosing = true;
				} else {
					//Gestion de l'erreur
					TypeCodeErreur.showError(result.codeErreur,this.translateService,this.toastrService);
				}
			});
	}

	/**
	 * Gère le changement de statut actif
	 * @param actif nouveau statut
	 */
	onActifChange(actif: boolean): void {
		this.externe.actif = actif;
		if (!actif && !this.externe.dateOut) {
			this.externe.dateOut = new Date();
		}
		//Recalcul du statut d'oubli lors du changement de statut actif
		this.calculerStatutOubli(this.delaiMois);
	}

	/**
	 * Gère le changement de date de sortie
	 * @param dateOut nouvelle date de sortie
	 */
	onDateOutChange(dateOut: Date): void {
		this.externe.dateOut = dateOut;

		//Recalcul du statut d'oubli lors du changement de la date de sortie
		this.calculerStatutOubli(this.delaiMois);
	}

	/**
	 * Enregistrement de l'externe
	 */
	onSave(): void {
		//Chargement en cours
		this.isPending = true;

		//Appel au service
		this.userService.saveExterne(this.externe)
			.pipe(first(),finalize(() => this.isPending = false))
			.subscribe((result: Result) => {
				//Vérification du result
				if (result.codeErreur === TypeCodeErreur.NO_ERROR) {
					//Toast succès
					this.toastrService.success(this.translateService.instant('global.success.enregistrement'));

					//Fermeture de la popin
					this.matDialogRef.close(true);
				} else {
					//Gestion de l'erreur
					TypeCodeErreur.showError(result.codeErreur,this.translateService,this.toastrService);
				}
			});
	}
}
